import {useEffect, useRef} from 'react'

type UseInfiniteScrollParams = {
  loadMore?: () => void
  hasMore?: boolean
  // The currently rendered items. Used only as a change signal: whenever this
  // array reference changes (a page was appended, or the list was replaced on a
  // sort/filter change) the hook re-checks whether more needs to load.
  items: readonly unknown[]
  // Distance below the viewport, in pixels, within which the sentinel counts as
  // "in the trigger zone". Mirrors the IntersectionObserver rootMargin.
  rootMargin?: number
}

// Drives infinite scroll for a window-scrolled list. Returns a ref to attach to
// a sentinel element placed after the last item.
//
// Two complementary triggers load the next page:
//  1. An IntersectionObserver fires while the user scrolls the sentinel into
//     view - the normal case.
//  2. A "fill" check runs after every list change. The observer only reacts to
//     scroll/layout transitions, so on its own it misses:
//       - the first page(s) exactly filling the viewport with the sentinel flush
//         against the bottom edge (there is never a scroll event to bring it into
//         view), and
//       - the list being replaced on a sort/filter change, which shrinks it back
//         under the fold while the sentinel is already in view.
//     In both cases the sentinel is sitting inside the trigger zone with no
//     transition to react to, so we measure it directly and keep loading until it
//     is pushed past the zone or there is nothing left to load.
export function useInfiniteScroll<T extends HTMLElement = HTMLDivElement>({
  loadMore,
  hasMore,
  items,
  rootMargin = 200,
}: UseInfiniteScrollParams) {
  const sentinelRef = useRef<T>(null)
  const loadMoreRef = useRef(loadMore)
  loadMoreRef.current = loadMore

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreRef.current?.()
      },
      {rootMargin: `${rootMargin}px`}
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [rootMargin])

  useEffect(() => {
    if (!hasMore) return
    const el = sentinelRef.current
    if (!el) return
    const triggerZoneBottom = window.innerHeight + rootMargin
    if (el.getBoundingClientRect().top <= triggerZoneBottom) {
      loadMoreRef.current?.()
    }
  }, [items, hasMore, rootMargin])

  return sentinelRef
}
